import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { IconButton, Menu, MenuItem } from '@material-ui/core'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import Icon from '../../../atoms/Icon'
import Tooltip from '../../../atoms/Tooltip'
import { useAppContext } from '../../../../redux/slices/appContext'
import FlexRow from '../../../molecules/FlexRow'

function DDPPhoneWidget ({
  pipeline,
  variables,
  size,
  accessor,
  label,
  tooltip,
  className,
  style
}) {
  const { isSummitUser } = useAppContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)
  const _phoneData = useMemo(() => {
    const phoneData = get(data, accessor)
    if (Array.isArray(phoneData)) {
      return phoneData
    } else {
      return phoneData ? [phoneData] : []
    }
  }, [data, accessor])

  const [anchorEl, setAnchorEl] = useState()
  const open = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setAnchorEl(e.target)
  }, [setAnchorEl])
  const close = useCallback(() => setAnchorEl(null), [])

  if (isLoading) {
    return null
  }

  if (!_phoneData) {
    return null
  }

  const disabled = !_phoneData?.length

  return (
    <div className='flex flex-col justify-center items-center gap-sm'>
      <Tooltip title={tooltip}>
        <IconButton className={className} style={{ ...(style || {}), backgroundColor: disabled ? 'var(--surface-default-disabled-bg)' : 'var(--surface-default-normal-bg)' }} onClick={open} disabled={disabled}>
          <Icon name='phone' customSize={size} color={disabled ? 'var(--text-color-disabled)' : 'var(--text-color-main)'} />
        </IconButton>
      </Tooltip>
      <div className='font-size-xs' style={{ color: disabled ? 'var(--text-color-disabled)' : 'var(--text-color-main)' }}>{label}</div>
      <Menu
        keepMounted
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
      >
        {(_phoneData || []).filter(a => !a.disabled).map((phone, index) => (
          <a href={isSummitUser ? 'tel:' : `tel:${phone.number}`} onClick={close} key={`$phone_${index}`}>
            <MenuItem>
              <FlexRow>
                <Icon name='phone' />
                <div>{phone.custom_type_title || phone.phone_type_description || phone.number}</div>
                {phone.is_preferred || phone.is_primary ? (
                  <span style={{ fontStyle: 'italic' }}>
                    {`(${phone.is_primary ? 'Primary' : ''}${phone.is_primary && phone.is_preferred ? ', ' : ''}${phone.is_preferred ? 'Preferred' : ''})`}
                  </span>
                ) : null}
              </FlexRow>
            </MenuItem>
          </a>
        ))}
      </Menu>
    </div>
  )
}

DDPPhoneWidget.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  variables: PropTypes.object,

  /** The size of the phone icon */
  size: PropTypes.number,

  /** The action label */
  label: PropTypes.string,

  /** The tool tip */
  tooltip: PropTypes.string,

  /** Gets the path to the phone data */
  accessor: PropTypes.string,

  className: PropTypes.string,
  style: PropTypes.object
}

DDPPhoneWidget.defaultProps = {
  size: 20,
  label: 'Call',
  tooltip: 'Make a call',
  className: 'radius-round flex items-center justify-center opal-surface-default hover color-neutral-500 shadow-md',
  style: {
    width: '50px',
    height: '50px'
  }
}

export default DDPPhoneWidget
